import type { WorkflowRun } from "@/lib/github/types";

/**
 * Returns true if this run is the first attempt (not a re-run).
 */
export function isFirstPass(run: WorkflowRun): boolean {
  return (run.run_attempt ?? 1) === 1;
}

/**
 * Filters a list of runs to first-pass only (run_attempt === 1).
 */
export function firstPassOnly(runs: WorkflowRun[]): WorkflowRun[] {
  return runs.filter(isFirstPass);
}

/**
 * Re-run rate = count(attempt > 1) / count(distinct run_id at attempt 1).
 * Returns 0..∞ — caller may want to clamp at 1 if treating as a ratio.
 * Returns 0 when there are no first-pass runs.
 *
 * Note: a run with attempt=1 that was never retried counts as the denominator.
 * Runs with attempt>1 that have no corresponding attempt=1 in the window are
 * still counted in the numerator (conservative approach).
 */
export function computeRerunRate(runs: WorkflowRun[]): number {
  // Distinct run ids that have at least one first-pass attempt in the dataset.
  const firstPassIds = new Set<number>();
  let rerunCount = 0;

  for (const run of runs) {
    const attempt = run.run_attempt ?? 1;
    if (attempt === 1) {
      firstPassIds.add(run.id);
    } else {
      rerunCount += 1;
    }
  }

  const denominator = firstPassIds.size;
  if (denominator === 0) return 0;
  return rerunCount / denominator;
}
